import { Chatbot, Grid, Modal, UI, Utils } from 'common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import DeleteRegionsModal from './DeleteRegionsModal.component';
import ManageRegionModal from './ManageRegionModal.component';

const { ModalContext } = Modal;
const {
	Logger: { logToServer },
	Session,
} = Utils;
const { Section, SectionBody, WrappedToolbar, Spinner, WrappedButtonType } = UI;
const { GridPanel } = Grid;
const { ChatbotTriggers, launchChatbot } = Chatbot;

const columnDefs = [
	{
		headerName: 'Region Name',
		field: 'Name',
		flex: 1,
		headerCheckboxSelection: true,
		checkboxSelection: true,
		suppressSizeToFit: false,
	},
];

class Regions extends Component {
	constructor(props) {
		super(props);

		this.state = {
			rowData: [],
			gridApi: null,
			numberOfRows: 0,
			isLoading: false,
			loadingMessage: '',
		};
	}

	getRegions = async (deselect = false) => {
		const { gridApi } = this.state;
		const { showError } = this.context;

		const response = await AccountCommunication.getRegions();

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				rowData: response.data,
			});
		} else {
			showError({ response });
		}

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});

		if (deselect) {
			gridApi.deselectAll();
		}
	};

	// set gridApi variable to AG Grid's api when the grid loads
	onGridReady = ({ api }) => {
		this.setState(
			{
				gridApi: api,
				isLoading: true,
				loadingMessage: 'Loading regions...',
			},
			this.getRegions
		);
	};

	// set AG Grid's sortmodel to default
	onSortChanged = sortModel => {
		const { gridApi } = this.state;
		gridApi.setSortModel(sortModel);
	};

	onSelectionChanged = () => {
		const { gridApi } = this.state;
		this.setState({ numberOfRows: gridApi.getSelectedRows().length });
	};

	launchAddModal = () => {
		const { rowData } = this.state;
		const { showModal } = this.context;

		showModal({
			component: ManageRegionModal,
			data: {
				onSuccess: this.onUpdateSuccess,
				rowData,
				modalType: 'add',
			},
		});

		logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'add_button',
		});
	};

	onUpdateSuccess = deselect => {
		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Updating regions...',
			},
			() => this.getRegions(deselect)
		);
	};

	launchUpdateModal = () => {
		const { gridApi, rowData } = this.state;
		const { showModal } = this.context;
		const { Id: regionId, Name: regionName } = gridApi.getSelectedRows()[0];

		showModal({
			component: ManageRegionModal,
			data: {
				onSuccess: this.onUpdateSuccess,
				regionId,
				rowData,
				modalType: 'update',
				regionName,
			},
		});

		logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'edit_button',
		});
	};

	launchDeleteModal = () => {
		const { gridApi } = this.state;
		const { showModal } = this.context;
		const selectedRows = gridApi.getSelectedRows().map(region => region.Id);

		showModal({
			component: DeleteRegionsModal,
			data: {
				onSuccess: this.onUpdateSuccess,
				selectedRows,
			},
		});

		logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'delete_button',
		});
	};

	launchFeedback = () => {
		const { showModal } = this.context;

		launchChatbot({
			flags: this.props.flags,
			showModal,
			trigger: ChatbotTriggers.FeedbackButton,
		});
	};

	render() {
		const { numberOfRows, rowData, isLoading, loadingMessage } = this.state;

		// buttons used for WrappedToolbar
		const buttons = [
			{
				label: 'Add',
				icon: 'add-icon',
				onClick: this.launchAddModal,
				tooltip: 'Add a new Region.',
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				label: 'Edit',
				disabled: numberOfRows !== 1,
				onClick: this.launchUpdateModal,
				tooltip: 'Edit the selected Region(s).',
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				label: 'Delete',
				disabled: numberOfRows === 0,
				onClick: this.launchDeleteModal,
				tooltip: 'Delete the selected Region(s)',
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				icon: 'feedback-icon',
				buttonType: WrappedButtonType.TertiaryWhite,
				label: 'Feedback',
				group: 'right',
				onClick: this.launchFeedback,
				tooltip: `${'Feedback'.bold()}\nTell us how we're doing!`,
				tooltipOptions: {
					position: 'bottom',
				},
			},
		];

		return (
			<div className="account-regions">
				<Section>
					<WrappedToolbar buttons={buttons} dataqa="regions-toolbar" />
					<SectionBody>
						<GridPanel
							rowData={rowData}
							columnDefs={columnDefs}
							showPagination={false}
							onGridReady={this.onGridReady}
							onSortChanged={this.onSortChanged}
							onSelectionChanged={this.onSelectionChanged}
							dataqa="regions-grid"
						/>
					</SectionBody>
				</Section>
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLDConsumer()(Regions);

Regions.contextType = ModalContext;
